// M004-F0745 sections 6.3 and 6.4 - the "Move to…" menu, ONE component in two skins: a popover under the selection bar on a desktop and a
// bottom sheet on a phone. role="menu" of envelopes (Unsorted first); a locked / closed / still-being-created envelope is listed but
// aria-disabled, its reason shown AND wired as the accessible description. Arrow keys / Home / End move, typing a letter jumps to the
// next envelope that starts with it, Enter or Space picks, Esc closes (the caller returns focus), Tab closes.
import React, { useEffect, useId, useRef } from 'react';

const TYPE_AHEAD_MS = 700;

function MenuList({ targets, onPick, onClose }) {
  const base = useId();
  const listRef = useRef(null);
  const typed = useRef({ text: '', at: 0 });

  useEffect(() => {
    const items = listRef.current ? Array.from(listRef.current.querySelectorAll('[role="menuitem"]')) : [];
    const first = items.find((item) => item.getAttribute('aria-disabled') !== 'true') || items[0];
    if (first) first.focus();
  }, []);

  const items = () => Array.from(listRef.current.querySelectorAll('[role="menuitem"]'));
  const focusAt = (index) => { const list = items(); if (list.length) list[(index + list.length) % list.length].focus(); };

  const onKeyDown = (event) => {
    const list = items();
    const index = list.indexOf(document.activeElement);
    if (event.key === 'ArrowDown') { event.preventDefault(); focusAt(index + 1); return; }
    if (event.key === 'ArrowUp') { event.preventDefault(); focusAt(index < 0 ? -1 : index - 1); return; }
    if (event.key === 'Home') { event.preventDefault(); focusAt(0); return; }
    if (event.key === 'End') { event.preventDefault(); focusAt(list.length - 1); return; }
    if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); onClose(); return; }
    if (event.key === 'Tab') { event.preventDefault(); onClose(); return; }
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      const target = targets[index];
      if (target && !target.reason) onPick(target);
      return;
    }
    if (event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
      const now = Date.now();
      typed.current = { text: (now - typed.current.at < TYPE_AHEAD_MS ? typed.current.text : '') + event.key.toLowerCase(), at: now };
      const from = typed.current.text.length > 1 ? index : index + 1;
      for (let step = 0; step < targets.length; step += 1) {
        const at = (from + step + targets.length) % targets.length;
        if (targets[at].label.toLowerCase().startsWith(typed.current.text)) { focusAt(at); break; }
      }
      event.preventDefault();
    }
  };

  return (
    <div className="ew-menu" role="menu" aria-label="Move to" data-testid="envelope-move-menu" ref={listRef} onKeyDown={onKeyDown}>
      {targets.map((target) => {
        const disabled = Boolean(target.reason);
        const reasonId = `${base}-${target.key}`;
        return (
          <button
            key={target.key}
            type="button"
            role="menuitem"
            className={`ew-menu-item${disabled ? ' ew-menu-item-disabled' : ''}`}
            tabIndex={-1}
            aria-disabled={disabled ? 'true' : undefined}
            aria-describedby={disabled ? reasonId : undefined}
            data-testid={`envelope-move-item-${target.id}`}
            onClick={() => { if (!disabled) onPick(target); }}
          >
            <span className="ew-menu-label">{target.label}</span>
            {disabled ? <span id={reasonId} className="ew-menu-reason" data-testid={`envelope-move-reason-${target.id}`}>{target.reason}</span> : null}
          </button>
        );
      })}
      {targets.length === 0 ? <p className="ew-muted ew-menu-empty">There is no other envelope yet. Name one in New envelope.</p> : null}
    </div>
  );
}

export default function ReceiptMoveMenu({ targets, count, asSheet, onPick, onClose }) {
  const titleId = useId();
  if (!asSheet) {
    return (
      <>
        <div className="ew-menu-scrim" aria-hidden="true" onMouseDown={onClose} />
        <div className="ew-popover" data-testid="envelope-move-popover"><MenuList targets={targets} onPick={onPick} onClose={onClose} /></div>
      </>
    );
  }
  return (
    <div className="ew-sheet-backdrop" data-testid="envelope-move-backdrop" onClick={onClose}>
      <div className="ew-sheet" role="dialog" aria-modal="true" aria-labelledby={titleId} data-testid="envelope-move-sheet" onClick={(event) => event.stopPropagation()}>
        <span className="ew-sheet-grip" aria-hidden="true" />
        <h3 id={titleId} className="ew-sheet-title">{`Move ${count} ${count === 1 ? 'receipt' : 'receipts'} to…`}</h3>
        <MenuList targets={targets} onPick={onPick} onClose={onClose} />
      </div>
    </div>
  );
}
